import type { AuthModel } from 'pocketbase';
import { v4 as uuidv4 } from 'uuid';

export const useDataStore = defineStore('data', () => {
  const DB = useDbStore();

  const user = ref<UserI | null>(null);

  const orders = ref<Record<string, OrderI>>({});

  const localOrders = useLocalStorage<Record<string, OrderI>>('orders', {});

  // cleans files empty objects after reload
  Object.values(localOrders.value).forEach((order) => {
    order.files = [];
  });

  const ordersPromises: Map<string, Promise<ServerOrderI | null>> = new Map();

  const myOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status !== 'cart' && order.status !== 'closed');
  });

  const closedOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'closed');
  });

  const cartOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'cart');
  });
  const forApproveOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'forapprove');
  });

  const ordersWithSubscriptions: Set<string> = new Set();

  // const list = computed(() => {
  //   return Array.from(orders.value.values());
  // });
  // // const

  const setUser = (model: UserI | AuthModel) => {
    if (!model) {
      return;
    }
    user.value = {
      avatar: model.avatar,
      email: model.email,
      name: model.name,
      verified: model.verified,
      deliveryDetails: model.deliveryDetails,
      billingDetails: model.billingDetails,
      phone: model.phone,
      cash: model.cash,
      id: model.id,
      loyaltyDiscount: model.loyaltyDiscount || 0,
    };
  };

  const setOrder = (order: ServerOrderI) => {
    // TODO replace OrderI with ServerOrderI
    orders.value[order.id] = convertServerOrderToOrder(order);
  };

  const subscribeOnUser = (id: string) => {
    // console.log(id);
    DB.subscribeToCollection('users', (data) => {
      // console.log(data.action, data.record);

      setUser(data.record);
      orders.value = {};
      data.record?.expand?.orders.forEach((order) => {
        setOrder(order);
        subscribeOnItem(order.id);
      });
    }, id, { expand: 'orders' });
  };

  const subscribeOnItem = (id: string) => {
    // console.log('subscribeOnItem', id);
    if (ordersWithSubscriptions.has(id)) return;
    ordersWithSubscriptions.add(id);
    // console.log(id);
    DB.subscribeToCollection(
      'orders',
      ({ action, record }) => {
        // console.log(action, record);
        if (action === 'update') {
          setOrder(record);
        }
      },
      id,
      { expand: 'user' },
    );
  };

  const initiateUserData = (model) => {
    if (model) {
      setUser(model);
      subscribeOnUser(model.id);

      model?.expand?.orders.forEach((order) => {
        setOrder(order);
        subscribeOnItem(order.id);
      });
    }
  };

  initiateUserData(DB.auth.model);

  const createOrder = async (item: ItemI, options: Partial<ServerOrderI> = {}) => {
    const localId = uuidv4();

    // const user = dataStore.user;
    const stickers = [...item.defaultStickers];
    stickers.forEach((sticker) => {
      // TODO: VALIDATE DATA FROM JSON/SERVER (IT CAN BE MISMATCHED !!!)
      sticker.instrumentType = 'sticker';
      sticker.scale = sticker.scale || { x: 1, y: 1 };
      if (!Object.hasOwnProperty.call(sticker, 'flipX')) sticker.flipX = false;
    });

    const order: ServerOrderI = {
      localId,
      itemName: item.name,
      itemType: item.type,
      customizer: {
        stickers,
        lines: [],
        pencils: [],
        comment: '',
      },
      comment: '',
      patient: '',
      file: null,
      files: [],
      approveFile: null,
      approveComments: { list: [] },
      approved: false,
      deliveryDetails: verifyDeliveryDetails(user?.deliveryDetails),
      billingDetails: verifyBillingDetails(user?.billingDetails),
      totalPrice: calculateOrderPrice({
        stickers,
        item,
      }, user.value?.loyaltyDiscount),
      paid: false,
      discount: 0,
      status: 'cart',

      user: user?.value?.id,
      ...options,
    };

    if (user.value) {
      // console.log(order);
      const sOrderPromise = DB.createOrder(order)
        .then((sOrder) => {
          setOrder(sOrder);
          return sOrder;
        });

      ordersPromises.set(order.localId, sOrderPromise);
      return sOrderPromise;
    }

    const localOrder = convertServerOrderToOrder(order);

    // console.log(localStorage);
    localOrders.value[order.localId] = localOrder;
    // console.log(localOrder);
    return localOrder;
    // const sOrder = await DB.createOrder(order);
    // console.log('ORDER FROM SERVER AFTER CREate', sOrder);

    // setOrder(sOrder);
  };

  const requestSyncOrder = async (order: OrderI) => {
    const o = convertOrderToServerOrder({ ...order, user: user.value?.id });
    // console.log('requestSyncOrder', o);

    const files = [...o.files];
    o.files.length = 0;

    const promise = DB.createOrder(o)
      .then((sOrder) => {
        delete localOrders.value[sOrder.localId];
        setOrder(sOrder);

        ordersPromises.delete(order.localId);

        if (files.length) {
          DB.addOrderFiles(sOrder.id, files);
        }
      });

    ordersPromises.set(order.localId, promise);
    return promise;
  };

  const syncOrdersWithDB = async () => {
    const keys = Object.keys(localOrders.value);
    const lId = keys[0];

    if (!lId) return;
    requestSyncOrder(localOrders.value[lId])
      .then(() => {
        syncOrdersWithDB();
      });
  };

  DB.onValidationChange((model) => {
    if (model) {
      initiateUserData(model);

      syncOrdersWithDB();
    }
    else {
      user.value = null;
      orders.value = {};
      DB.unsubscribeFromCollection('users');
      ordersWithSubscriptions.clear();
      DB.unsubscribeFromCollection('orders');
    }
  });

  return {
    user,
    orders,
    localOrders,

    myOrders,
    cartOrders,
    forApproveOrders,
    closedOrders,

    setOrder,
    createOrder,

    ordersPromises,
  };
});
